import dayjs from 'dayjs';
import React, { Ref, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getLanguage } from '@travel/i18n';
import { EMPTY_ARRAY, EMPTY_FUNCTION } from '@travel/traveler-core/constants';
import { MOBILE_SCREEN, useDeviceType, useDialogHandler } from '@travel/traveler-core/hooks';
import { useDatesChange } from '@travel/traveler-core/hooks/useDatesChange';
import { PROVIDER_LIST_TYPE } from '@travel/traveler-core/types/providerInformation';
import { FlatButton } from '@travel/ui';
import { cx, parseQueryString, stringify } from '@travel/utils';

import { AmountInputRef } from 'components/AmountInput';
import AmountInputWithRef from 'components/AmountInput/AmountInputWithRef';
import { DateRef } from 'components/DateInput';
import DateInputWithRef from 'components/DateInput/DateInputWithRef';
import Form from 'components/Form';
import PlaceInput from 'components/PlaceInput';
import SalesPromotionInput from 'components/SalesPromotionInput';

import { replaceLocation } from 'store/__router/actions';
import { getItem as getProviderItem } from 'store/providerInformation/selectors';
import { updateAreaName } from 'store/providerList/actions';

import { TAB_ID } from 'constants/providerInfoTabIds';
import { Translate } from 'core/translate';
import paths from 'core/universalRouter/paths';
import usePlaceRedirect from 'hooks/usePlaceRedirect';
import { ProviderInfoListTypes, ProviderInfoQuery } from 'ProviderInformation-Types';
import { SuggestionsItem } from 'Suggestions-Types';
import { savedSearchStore } from 'utils/savedSearch';
import { getBasicRedirectSearchQuery } from 'utils/search';

import HiddenFilterForm, { FilterFormProps } from '../HiddenFilterForm';
import SearchInfoSP from './SearchInfoSP';

import styles from './searchBar.module.scss';

export type SearchBarRef = {
  onSubmit: () => void;
} | null;

type Props = {
  className?: string;
  pageIdentifier: string;
  query: ProviderInfoQuery;
  /** Whether date picker auto open after selected place */
  isAutoOpenDatePicker?: boolean;
  /** Set auto open date picker */
  setIsAutoOpenDatePicker?: (autoOpen: boolean) => void;
  /** Values from filter form */
  filterFormProps: FilterFormProps;
  /** Active tab id in SP */
  activeTabId?: string;
  /** Active list type, room or plan */
  listType?: ProviderInfoListTypes;
  /** Form Ref */
  innerRef?: Ref<SearchBarRef>;
};

function SearchBar(props: Props) {
  const {
    className,
    pageIdentifier,
    query,
    isAutoOpenDatePicker,
    setIsAutoOpenDatePicker,
    activeTabId,
    listType,
    innerRef,
  } = props;

  const dispatch = useDispatch();

  const providerItem = useSelector(getProviderItem);
  const language = useSelector(getLanguage);

  const placeRedirect = usePlaceRedirect();

  const [isError, setIsError] = useState(false);
  const [currentPlace, setCurrentPlace] = useState<SuggestionsItem>({
    id: pageIdentifier,
    pathId: pageIdentifier,
    type: 'SPOT',
    name: providerItem.name,
    category: 'PROVIDER',
  });

  useEffect(() => {
    setCurrentPlace(currentPlace => ({ ...currentPlace, name: providerItem.name }));
  }, [providerItem.name]);

  const [startDate, setStartDate] = useState(query.startDate || '');
  const [endDate, setEndDate] = useState(query.endDate || '');

  const isSP = useDeviceType() === MOBILE_SCREEN;
  const {
    isOpen: isExpandPanel,
    onToggle: onToggleSearchPanel,
    onClose: onCloseSearchPanel,
  } = useDialogHandler(false);

  const formEl = useRef<HTMLFormElement | null>(null);
  const dateRef = useRef<DateRef>(null);
  const amountInputRef = useRef<AmountInputRef>(null);

  const onPage = query.startDate || query.endDate ? 'providerInfoDated' : 'providerInfoUndated';
  const { isDatesSet, onDatesSet } = useDatesChange(
    Boolean(query?.startDate) && Boolean(query?.endDate),
  );

  const isCurrentProvider = useCallback(
    (place: SuggestionsItem) => {
      return place.category === 'PROVIDER' && place.id === providerItem.id;
    },
    [providerItem.id],
  );

  const getFormQueryString = useCallback(() => {
    if (!formEl.current) {
      return '';
    }

    const formData = new FormData(formEl.current);
    const search = new URLSearchParams(formData as any);
    isSP && search.set('activeTabId', activeTabId || TAB_ID.OVERVIEW);
    search.set('listType', listType || PROVIDER_LIST_TYPE.ROOM_LIST);
    return search.toString();
  }, [activeTabId, isSP, listType]);

  const onSubmit = useCallback(
    (place: SuggestionsItem) => {
      if (!formEl.current) {
        return;
      }
      if (!place.pathId) {
        setIsError(true);
        return;
      }

      let queryString = getFormQueryString();

      if (!isCurrentProvider(place)) {
        // New place
        const queryObject = parseQueryString(queryString);
        queryString = stringify(getBasicRedirectSearchQuery(queryObject));

        if (place.category !== 'PROVIDER') {
          dispatch(updateAreaName(place.name));
        }

        placeRedirect(place, queryString);
      } else {
        // Current provider
        dispatch(
          replaceLocation({
            pathname: paths.providerInfo.pathResolver(place.pathId),
            search: queryString,
            state: { isUpdate: true },
          }),
        );
      }
      const formData = new FormData(formEl.current);
      const queryObj = Object.fromEntries(formData.entries());
      savedSearchStore(language, place, queryObj);
    },
    [dispatch, getFormQueryString, isCurrentProvider, language, placeRedirect],
  );

  const onUpdateSearch = useCallback(
    (place?: SuggestionsItem) => {
      onSubmit(place || currentPlace);
    },
    [currentPlace, onSubmit],
  );

  useImperativeHandle(innerRef, () => ({
    onSubmit: () => {
      onSubmit(currentPlace); // For filters to call
    },
  }));

  const onChangePlace = useCallback(
    (place: SuggestionsItem) => {
      setCurrentPlace(place);

      if (place.pathId) {
        setIsError(false);
      } else {
        if (isSP && !place.name) {
          setIsError(true);
        }
      }

      if (place.isSavedSearch && place.query) {
        // setDate
        const startDate = place.query.startDate && place.query.startDate.toString();
        const endDate = place.query.endDate && place.query.endDate.toString();
        // the dates needs to be set for the childrenAges to be set
        let shouldSetAmount = true;
        if (startDate && endDate) {
          if (dayjs(startDate).isSameOrBefore(dayjs().subtract(1, 'day'))) {
            shouldSetAmount = false;
            dateRef.current?.resetDate();
            setStartDate('');
            setEndDate('');
            onDatesSet('', '');
            if (!isSP) {
              setIsAutoOpenDatePicker?.(true);
              dateRef.current?.focus();
            }
          } else {
            setStartDate(startDate);
            setEndDate(endDate);
            onDatesSet(startDate, endDate);
          }
        }
        // set rooms/guests/children amount
        if (shouldSetAmount) {
          amountInputRef.current?.setAmount({
            rooms: place.query.noOfUnits ? Number(place.query.noOfUnits) : undefined,
            adults: place.query.adults,
            childrenAges: place.query.childrenAges,
          });
        }
      }

      if (place.pathId && isSP) {
        onUpdateSearch(place);
      }
    },
    [isSP, onDatesSet, onUpdateSearch, setIsAutoOpenDatePicker],
  );

  const onSelectPlace = useCallback(
    (isSavedSearch?: boolean, hasDate?: boolean) => {
      if (isSavedSearch && !hasDate && isDatesSet) {
        dateRef.current?.resetDate();
        setStartDate('');
        setEndDate('');
        onDatesSet('', '');
      }
    },
    [isDatesSet, onDatesSet],
  );

  const placeInput = (
    <div className={styles.placeInput}>
      <PlaceInput
        onPage={onPage}
        defaultText={providerItem.name || ''}
        onChangePlace={onChangePlace}
        onSelectPlace={onSelectPlace}
        hasError={isError}
        errorMessage={
          <Translate
            id="Traveler_Input_Error.Global_Search.Destination_Search.No_Input"
            className={styles.errorMessage}
          />
        }
        errorMessageClassName={styles.errorMessageWrapper}
        hasAnimation={true}
      />
    </div>
  );

  const dateInput = (
    <DateInputWithRef
      ref={dateRef}
      className={styles.dateInput}
      onPage={onPage}
      startDate={startDate}
      endDate={endDate}
      onClickOkButton={isSP ? onUpdateSearch : undefined}
      onDateChanged={onDatesSet}
      isAutoOpenDatePicker={isAutoOpenDatePicker}
      setIsAutoOpenDatePicker={setIsAutoOpenDatePicker}
      hasAnimationDialog={true}
    />
  );

  const amountInput = (
    <AmountInputWithRef
      ref={amountInputRef}
      onClickOkButton={isSP ? () => onUpdateSearch() : undefined}
      className={styles.amountInput}
      onPage={onPage}
      defaultAdults={Number(query.adults)}
      defaultRooms={Number(query.noOfUnits)}
      defaultChildrenAges={
        query.childrenAges ? query.childrenAges.split(',').map(Number) : EMPTY_ARRAY
      }
      isDatesSet={isDatesSet}
      onCloseClick={() => onUpdateSearch()}
      hasAnimationDialog={true}
    />
  );

  return (
    <>
      <Form
        ref={formEl}
        className={cx(className, styles.wrapper)}
        onSubmit={() => onUpdateSearch()}
        data-testid="searchBar-wrapper"
      >
        <HiddenFilterForm {...props.filterFormProps} />
        <SalesPromotionInput {...query} />
        {!isSP && (
          <>
            <div className={styles.searchFormContainer}>
              {/* Translation off the translation for the date input section: https://jira.rakuten-it.com/jira/browse/TIDP-28464 */}
              <div className={cx(styles.searchForm, isError && styles.hasError)} translate="no">
                {placeInput}
                {dateInput}
                {amountInput}
                <FlatButton
                  className={styles.submitButton}
                  type="submit"
                  data-testid="providerInfo-searchFrom-submitButton"
                >
                  <Translate id="Provider_Info.Update" />
                </FlatButton>
              </div>
            </div>
          </>
        )}
        {isSP && (
          <>
            <div className={cx(styles.searchForm)}>
              <div className={styles.inputsWrapper}>
                <SearchInfoSP
                  query={query}
                  onClick={onToggleSearchPanel}
                  destination={providerItem.name}
                />
              </div>
              <div
                className={cx(styles.expandPanel, !isExpandPanel && styles.hidden)}
                translate="no"
                data-testid="searchBar-expandPanel-wrapper"
              >
                {placeInput}
                {dateInput}
                {amountInput}
              </div>
            </div>
          </>
        )}
      </Form>
      {isExpandPanel && (
        <button
          tabIndex={-1}
          className={styles.overlay}
          onTouchStart={onCloseSearchPanel}
          onKeyDown={EMPTY_FUNCTION}
        />
      )}
    </>
  );
}

export const SearchBarWithRef = React.memo(
  React.forwardRef((props: Props, ref: React.Ref<SearchBarRef>) => (
    <SearchBar {...props} innerRef={ref} />
  )),
);

export default React.memo(SearchBar);
